// src/slices/ticketDelivery/reducer.ts
// Redux slice cho phân hệ Giao vé cho đại lý

import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TicketDelivery,
  TicketDeliveryFilters,
  TicketDeliveryModalState,
  TicketDeliveryPagination,
} from 'pages/TicketDeliveryManagement/types';
import { TicketDeliveryState } from './types';
import {
  fetchTicketDeliveries,
  fetchTicketDeliveryById,
  fetchTicketStockBalances,
  createTicketDelivery,
  updateTicketDelivery,
  confirmTicketDelivery,
  cancelTicketDelivery,
  agentConfirmTicketDelivery,
} from './thunk';

const initialFilters: TicketDeliveryFilters = {
  planId: '',
  agentId: '',
  status: 'All',
};

const initialState: TicketDeliveryState = {
  list: [],
  selectedItem: null,
  stockBalances: [],
  pagination: {
    currentPage: 1,
    pageSize: 20,
    totalItems: 0,
    totalPages: 0,
  },
  filters: { ...initialFilters },
  loading: false,
  loadingStock: false,
  submitting: false,
  error: null,
  modalState: {
    create: false,
    confirm: false,
    cancel: false,
    detail: false,
    agentConfirm: false,
  },
};

const ticketDeliverySlice = createSlice({
  name: 'TicketDelivery',
  initialState,
  reducers: {
    setSelectedItem: (state, action: PayloadAction<TicketDelivery | null>) => {
      state.selectedItem = action.payload;
    },
    setPage: (state, action: PayloadAction<number>) => {
      state.pagination.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pagination.pageSize = action.payload;
      state.pagination.currentPage = 1;
    },
    setPagination: (state, action: PayloadAction<Partial<TicketDeliveryPagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    setFilters: (state, action: PayloadAction<Partial<TicketDeliveryFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.currentPage = 1;
    },
    clearFilters: (state) => {
      state.filters = { ...initialFilters };
      state.pagination.currentPage = 1;
    },
    clearStockBalances: (state) => {
      state.stockBalances = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    openModal: (state, action: PayloadAction<keyof TicketDeliveryModalState>) => {
      state.modalState[action.payload] = true;
    },
    closeModal: (state, action: PayloadAction<keyof TicketDeliveryModalState>) => {
      state.modalState[action.payload] = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // Danh sách phiếu giao
      .addCase(fetchTicketDeliveries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTicketDeliveries.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload as any;
        state.list = payload?.items || payload?.data || payload || [];
        state.pagination.totalItems = payload?.totalItems ?? payload?.totalCount ?? state.list.length;
        state.pagination.totalPages =
          payload?.totalPages ??
          Math.ceil(state.pagination.totalItems / Math.max(state.pagination.pageSize, 1));
      })
      .addCase(fetchTicketDeliveries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Chi tiết phiếu giao
      .addCase(fetchTicketDeliveryById.fulfilled, (state, action) => {
        state.selectedItem = action.payload as TicketDelivery;
      })

      // Tồn kho theo Symbol
      .addCase(fetchTicketStockBalances.pending, (state) => {
        state.loadingStock = true;
      })
      .addCase(fetchTicketStockBalances.fulfilled, (state, action) => {
        state.loadingStock = false;
        const payload = action.payload as any;
        state.stockBalances = payload?.items || payload?.data || payload || [];
      })
      .addCase(fetchTicketStockBalances.rejected, (state, action) => {
        state.loadingStock = false;
        state.stockBalances = [];
        state.error = action.payload as string;
      })

      // Tạo phiếu giao
      .addCase(createTicketDelivery.pending, (state) => {
        state.submitting = true;
        state.error = null;
      })
      .addCase(createTicketDelivery.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.create = false;
      })
      .addCase(createTicketDelivery.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Sửa phiếu giao (còn Draft)
      .addCase(updateTicketDelivery.pending, (state) => {
        state.submitting = true;
      })
      .addCase(updateTicketDelivery.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload as TicketDelivery;
        const index = state.list.findIndex((item) => item.id === updated?.id);
        if (index !== -1) state.list[index] = updated;
      })
      .addCase(updateTicketDelivery.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Xác nhận giao vé
      .addCase(confirmTicketDelivery.pending, (state) => {
        state.submitting = true;
      })
      .addCase(confirmTicketDelivery.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload as TicketDelivery;
        const index = state.list.findIndex((item) => item.id === updated?.id);
        if (index !== -1) state.list[index] = updated;
        state.modalState.confirm = false;
        state.selectedItem = null;
      })
      .addCase(confirmTicketDelivery.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Huỷ phiếu giao
      .addCase(cancelTicketDelivery.pending, (state) => {
        state.submitting = true;
      })
      .addCase(cancelTicketDelivery.fulfilled, (state, action) => {
        state.submitting = false;
        const updated = action.payload as TicketDelivery;
        const index = state.list.findIndex((item) => item.id === updated?.id);
        if (index !== -1) state.list[index] = updated;
        state.modalState.cancel = false;
        state.selectedItem = null;
      })
      .addCase(cancelTicketDelivery.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      })

      // Đại lý xác nhận đã nhận vé
      .addCase(agentConfirmTicketDelivery.pending, (state) => {
        state.submitting = true;
      })
      .addCase(agentConfirmTicketDelivery.fulfilled, (state) => {
        state.submitting = false;
        state.modalState.agentConfirm = false;
        state.selectedItem = null;
      })
      .addCase(agentConfirmTicketDelivery.rejected, (state, action) => {
        state.submitting = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSelectedItem,
  setPage,
  setPageSize,
  setPagination,
  setFilters,
  clearFilters,
  clearStockBalances,
  clearError,
  openModal,
  closeModal,
} = ticketDeliverySlice.actions;

export default ticketDeliverySlice.reducer;
